// Thin fetch wrapper — every coach API call lives behind a typed function
// that goes here. Keeps fetch() noise out of components; the fetch core
// (JSON in/out, typed ApiError, one-time 401 redirect hook) is shared with
// every other Region 13 SPA via @region13/ui (see admin-spa/src/lib/api.ts).

import { createApiClient, ApiError } from '@region13/ui'
import type {
  CoachPracticeView,
  Me,
  PracticeFeasibilityResult,
  PracticeFormVariant,
  PracticePairing,
  PracticePreference,
  PracticePriorityAxis,
  PracticeWeekday,
  RefPosition,
  ScoreInput,
} from '@region13/shared-types'

const API_BASE = '/api'

// Unlike admin-spa (Cloudflare Access — a 401 means "go re-authenticate with
// Access", so a hard reload is correct), coach.ayso13.org uses email-code
// login: a 401 just means "not signed in yet", which routes/__root.tsx
// already renders (the login screen) from a plain `me.email === null`
// response. So no onAuthRedirect here — a 401 should surface as a normal
// query error, not force-reload the page.
const { get, post, put } = createApiClient({ base: API_BASE })

/** A single game on a coach's team schedule — mirrors the `games` row shape
 *  returned by `GET /api/coach/teams` (apps/coach/src/index.ts). */
export interface CoachGame {
  id: string
  date: string
  time: string
  field_name: string | null
  gameNum: string | null // InLeague game number, when captured
  /** `games.week`: the week filter and Hide past weeks key on it. */
  week: number
  team1: string
  // Null for an Extra team's home fixture (the opponent is not one of our
  // teams) — see `external_opponent` below, which carries the bare label for
  // that case.
  team2: string | null
  /** Set only for an Extra team's home fixture (design spec §7). The bare
   *  opponent text `external_fixtures.opponent_label` holds, before
   *  `lib/nextGame.ts opponentOf` composes the "· Extra match" marker. */
  external_opponent: string | null
}

/** One of a team's away fixtures (`external_fixtures.kind = 'away'`) — mirrors
 *  `GET /api/coach/teams/:ext/away-fixtures`. These never become a `games` row
 *  (only a home fixture does), so they render on their own "Away games" card
 *  rather than in the schedule list. */
export interface CoachAwayFixture {
  date: string
  opponent_label: string
  location_text: string | null
  known_time: string | null
}

/** A team the signed-in coach is assigned to this season, plus its upcoming
 *  games. Mirrors `CoachTeam` (`@region13/inleague/coach`) + the `games` array
 *  attached by `GET /api/coach/teams`. Note: the fields are `externalId`/
 *  `name`/`division`, not `teamDesignation`/`teamName`/`title`.
 *
 *  ⚠️ `name` is InLeague's season-scoped team name or null - NULL for nearly
 *  every team (`docs/DECISIONS.md`). Never render it bare; compose a label
 *  with `teamLabel` (`@region13/shared-types`), which is also what `coachName`
 *  exists to feed. */
export interface CoachTeamWithGames {
  externalId: string
  name: string | null
  coachName: string | null
  division: string
  inleagueTeamUid: string | null
  /** 1 when this team's division uses game cards, 0 when it does not (below
   *  10U, or a division whose games are not ours). Derived server-side from the
   *  one shared predicate — see `collectsGameCards` in the coach worker's
   *  dao/coachTeams. A number because SQLite has no boolean. */
  gameCards: number
  /** 1 when the signed-in person is this team's head coach, the only person
   *  who may rename it. */
  isHeadCoach: number
  /** True when the team holds a flight in a published Rose City Cup, so it may print the
   *  players-only Cup card. Always sent by the server; a missing value reads as false, the closed
   *  direction, since the route refuses independently. */
  cup?: boolean
  games: CoachGame[]
}

/** One game on the Games page. Mirrors `TeamSeasonGame`
 *  (`apps/coach/src/dao/teamSeasonGames.ts`), returned by
 *  `GET /api/coach/teams/:ext/season-games`. The schedule fields are
 *  `CoachGame`'s, so `gameLine` and `opponentOf` format both. `score` and
 *  `sportsmanship` are null until the game's week is published. */
export interface SeasonGame extends CoachGame {
  /** Which side this team played, for reading `score` from its side. */
  side: 'home' | 'visitor'
  isFriendly: boolean
  /** `games.status`; label it with `gameStatusLabel` when it is not 'scheduled'. */
  gameStatus: string
  /** Stats collects a card for this game. False for an Area/Section fixture
   *  or a division below the card age: no result will ever arrive. */
  tracked: boolean
  published: boolean
  /** The recorded score, raw; derive the final with `deriveResult`. */
  score: ScoreInput | null
  /** This team's points, 0 to 5. */
  sportsmanship: number | null
}

/** The volunteers assigned to a team in InLeague (Home dashboard). Mirrors
 *  `GET /api/coach/teams/:ext/volunteers`. */
export type VolunteerRole = 'Head coach' | 'Assistant coach' | 'Referee' | 'Manager'
export interface Volunteer { name: string | null; email: string | null; phone: string | null; roles: VolunteerRole[] }
export interface TeamVolunteers {
  coach: Volunteer | null
  assistant: Volunteer | null
  referees: (Volunteer & { grade: string | null })[]
  managers: Volunteer[]
}

/** One referee slot on a game (Referees tab). `assigneeState === null` = open
 *  (the name can be null even when filled — use email as a fallback label).
 *  `ageGroup` and `isOptional` feed `positionLabelFor`/the coverage count —
 *  see `apps/coach/src/dao/refereeCoverage.ts`, which this mirrors. */
export interface CrewSlot {
  position: RefPosition
  ageGroup: string
  responsibility: string
  isOwnTeam: boolean
  isOptional: boolean
  assigneeState: string | null
  assigneeName: string | null
  assigneeEmail: string | null
}
/** A game's full referee crew. Mirrors `GET /api/coach/teams/:ext/referees`. */
export interface GameCrew {
  game: { id: string; date: string | null; time: string; field_name: string | null; gameNum: string | null; week: number; opponent: string; status: string }
  slots: CrewSlot[]
}

/** One ranked team's row, as `@region13/core/dao/standings` (`RankedStandingsTeam`)
 *  returns it — mirrored here rather than imported, because that package is a
 *  Worker-side module (D1 types) this browser bundle never depends on. Every
 *  field `rankLabel` (`@region13/shared-types`) or a direct render needs;
 *  nothing else.
 *
 *  ⚠️ `externalId` IS THE TEAM'S NAME HERE. There is no friendly-name field:
 *  see `StandingsTeamRow`'s own doc in `@region13/core/dao/standings` for why
 *  the designation is the identifier on every standings surface. */
export interface StandingsTeamRow {
  externalId: string
  pool: string | null
  played: number
  won: number
  tied: number
  lost: number
  goalsFor: number
  goalsAgainst: number
  points: number
  sportsmanship: number
  sportsmanshipPossible: number
  refereePoints: number
  rank: number
  sharesRank: boolean
}

/** Mirrors `DivisionStandings` (`@region13/core/dao/standings`). */
export interface CoachDivisionStandings {
  divisionId: string
  divisionName: string
  pools: { pool: string | null; teams: StandingsTeamRow[] }[]
}

/** Mirrors `GET /api/coach/teams/:ext/standings`'s response
 *  (`apps/coach/src/dao/coachStandings.ts` `CoachStandings`). Both fields null
 *  together means nothing is published yet; `division` alone null with
 *  `publishedThrough` set means this team's division has no ranked table. */
export interface CoachStandingsResponse {
  division: CoachDivisionStandings | null
  publishedThrough: number | null
}

/** A roster player, with any saved jersey number joined in. Mirrors
 *  `GET /api/coach/teams/:ext/roster`'s per-player response shape. */
export interface RosterPlayerV {
  childId: string
  registrationId: string
  firstName: string
  lastName: string
  /** InLeague's nickname, cleaned (#854). Null when there is none; absent on a
   *  live-fallback roster. */
  preferredName?: string | null
  jersey: number | null
}

/** One jersey-number save entry, as sent to `PUT /api/coach/teams/:ext/jerseys`. */
export interface JerseyEntryV {
  player_id: string
  jersey_number: number
  registration_id?: string | null
}

/** Practice-prefs write payload, as sent to
 *  `PUT /api/coach/teams/:ext/practice-prefs` — mirrors the validated shape
 *  `apps/coach`'s `validatePrefsInput` accepts, sans the read-only fields
 *  (`seasonId`/`teamExternalId`/`submittedByEmail`/`updatedAt`) the read-side
 *  `PracticePreference` also carries. Practice length is not a coach input:
 *  the admin sets it per division, so there is no override field here. */
export interface PracticePrefsInput {
  dayPref: PracticeWeekday[]
  /** Ranked days for a UD team's SECOND weekly practice (spec D4). Null on
   *  the standard form (Task 5); the Upper Division form (Task 7) is what
   *  actually sets this. Required, not optional: an omitted field is exactly
   *  the silent-drop bug the server's `?? []`/`?? null` defaults would mask. */
  dayPref2: PracticeWeekday[] | null
  /** HARD: days the coach says do not work at all (spec D3). Required for the
   *  same reason as `dayPref2` above. */
  dayBlocked: PracticeWeekday[]
  timeEarliest: string | null
  timeLatest: string | null
  /** Soft target inside the hard earliest/latest bounds (spec D9). */
  timePreferred: string | null
  /** Which of day-of-week or venue to sacrifice first (spec D4). Always sent
   *  as a real value: 'day' by default, never omitted. */
  priorityAxis: PracticePriorityAxis
  venuePref: string[]
  note: string | null
}

/** Mirrors `GET /api/coach/teams/:ext/report-links`: absolute URLs into the stats report wizards. */
export type ReportLinks = { incident: string; coachFeedback: string; refereeFeedback: string }

export const api = {
  // ── Auth (email-code) ─────────────────────────────────────────────────
  me: () => get<Me>('/auth/me'),
  requestCode: (email: string, turnstileToken: string | null) =>
    post<{ ok: boolean; dev_code?: string }>('/auth/request-code', {
      email,
      turnstile_token: turnstileToken,
    }),
  verifyCode: (email: string, code: string) => post<Me>('/auth/verify-code', { email, code }),
  logout: () => post<{ ok: boolean }>('/auth/logout', {}),

  // ── Coach (coach:read) ────────────────────────────────────────────────
  // For a superadmin, `teams` returns every season team as a lightweight
  // selector list (each with `games: []`); `teamGames` loads the chosen team's
  // games on demand. A regular coach gets their team(s) with games inline.
  teams: () => get<{ teams: CoachTeamWithGames[] }>('/coach/teams'),
  teamGames: (ext: string) => get<{ games: CoachGame[] }>(`/coach/teams/${ext}/games`),
  awayFixtures: (ext: string) => get<{ fixtures: CoachAwayFixture[] }>(`/coach/teams/${ext}/away-fixtures`),
  renameTeam: (ext: string, name: string, expected: string | null) =>
    put<{ ok: true; name: string | null }>(`/coach/teams/${ext}/name`, { name, expected }),
  volunteers: (ext: string) => get<TeamVolunteers>(`/coach/teams/${ext}/volunteers`),
  refereeCoverage: (ext: string) => get<{ games: GameCrew[] }>(`/coach/teams/${ext}/referees`),
  standings: (ext: string) => get<CoachStandingsResponse>(`/coach/teams/${ext}/standings`),
  seasonGames: (ext: string) => get<{ games: SeasonGame[] }>(`/coach/teams/${ext}/season-games`),
  reportLinks: (ext: string, gameId?: string) =>
    get<ReportLinks>(`/coach/teams/${ext}/report-links${gameId ? `?gameId=${encodeURIComponent(gameId)}` : ''}`),
  formLink: (ext: string) => get<{ url: string | null }>(`/coach/teams/${ext}/form-link`),
  roster: (ext: string) => get<{ players: RosterPlayerV[] }>(`/coach/teams/${ext}/roster`),
  saveJerseys: (ext: string, entries: JerseyEntryV[]) =>
    put<{ ok: boolean }>(`/coach/teams/${ext}/jerseys`, { entries }),
  practicePrefs: (ext: string) =>
    get<{ prefs: PracticePreference | null; variant: PracticeFormVariant }>(`/coach/teams/${ext}/practice-prefs`),
  savePracticePrefs: (ext: string, prefs: PracticePrefsInput) =>
    put<{ ok: boolean }>(`/coach/teams/${ext}/practice-prefs`, prefs),
  // Per-team feasible venues (age-eligible + a long-enough permit window).
  // This REPLACED a season-wide venue list (`GET /coach/practice/venues`),
  // which is why no client for that route exists here any more: a coach must
  // not be able to rank a venue or day their team can never actually get.
  practiceFeasibility: (ext: string) =>
    get<PracticeFeasibilityResult>(`/coach/teams/${encodeURIComponent(ext)}/practice-feasibility`),
  practiceSlot: (ext: string) => get<CoachPracticeView>(`/coach/teams/${ext}/practice`),
  // Coach-level (not team-scoped): a multi-team coach's back-to-back wish.
  // `null` clears the wish — the backend DELETEs the row on `null`, so a
  // client must never send '' or undefined as a stand-in for "no preference".
  practicePairing: () => get<{ pairing: PracticePairing | null }>('/coach/practice-pairing'),
  putPracticePairing: (pairing: PracticePairing | null) =>
    put<{ ok: boolean }>('/coach/practice-pairing', { pairing }),
  cardUrl: (ext: string, gameId: string) => `/api/coach/teams/${ext}/games/${gameId}/card`,
  blankCardUrl: (ext: string) => `/api/coach/teams/${ext}/blank-card`,
  // The players-only Cup card: the roster and no game details, so it works for any Cup game.
  cupCardUrl: (ext: string) => `/api/coach/teams/${ext}/cup-card`,
  // Two-up sheets: two cards side by side on one landscape page. Neither takes
  // a gameId — the scheduler resolves this team's next two games itself.
  pairCardUrl: (ext: string) => `/api/coach/teams/${ext}/pair-card`,
  blankPairCardUrl: (ext: string) => `/api/coach/teams/${ext}/blank-pair-card`,
}

export { ApiError }

/** Does the coach portal offer a game card for this team?
 *
 *  Mirrors the coach worker's `collectsGameCards` so the 0/1 D1 returns is read
 *  the same way on both sides, and so the SPA spells the rule ONCE. Three sites
 *  need it (the cards panel, its games query, and Home's quick link), and
 *  `gameCards` is typed `number`: today `=== 1` and a truthiness check agree
 *  only because the server's CASE emits exactly 0 or 1, and they would part
 *  company the moment that expression could yield NULL or a wider encoding.
 *
 *  A missing field reads as false. That is the fail-closed direction, which is
 *  right for the affordance (the route refuses independently either way) and is
 *  pinned server-side by the DAO's own SQL-shape tests. */
export function collectsGameCards(team: Pick<CoachTeamWithGames, 'gameCards'>): boolean {
  return team.gameCards === 1
}
